import { useMemo, useState, useEffect } from 'react';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useScrollToTop } from '@frontend/analytics-shared';
import { Icon, IconName } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Table, TableColumnConfig, TableProps, Text } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../hooks/use-appointment-analytics-v2-store';

type dataValueType = {
  value: number;
  change: number;
};

type ColumnConfig = TableColumnConfig<LocationRowData> & {
  startAdornment?: string;
  endAdornment?: string;
};

const DEFAULT_PAGE_SIZE = 25;

const getColumns = (activeTab: string): ColumnConfig[] => {
  if (activeTab === APPOINTMENT_TABS.UPCOMING) {
    return [
      {
        id: 'scheduledAppointments',
        Header: 'Scheduled',
        accessor: 'scheduledAppointments',
      },
      {
        id: 'confirmedAppointments',
        Header: 'Confirmed',
        accessor: 'confirmedAppointments',
      },
      {
        id: 'atRiskAppointments',
        Header: 'At-Risk',
        accessor: 'atRiskAppointments',
      },
    ];
  }
  return [
    {
      id: 'scheduledAppointments',
      Header: 'Scheduled',
      accessor: 'scheduledAppointments',
    },
    {
      id: 'completedAppointments',
      Header: 'Completed',
      accessor: 'completedAppointments',
    },
    {
      id: 'cancelledAppointments',
      Header: 'Cancelled',
      accessor: 'cancelledAppointments',
    },
    {
      id: 'confirmedAppointments',
      Header: 'Confirmed',
      accessor: 'confirmedAppointments',
    },
    {
      id: 'averageAppointmentsPerDay',
      Header: 'Avg Appointments Per Day',
      accessor: 'averageAppointmentsPerDay',
    },
  ];
};

interface CompareLocationsTableProps {
  filters: {
    locationId?: string[];
    startDate?: string;
    endDate?: string;
  };
}

interface LocationRowData {
  key: string;
  scheduledAppointments: dataValueType;
  confirmedAppointments: dataValueType;
  atRiskAppointments?: dataValueType;
  completedAppointments?: dataValueType;
  cancelledAppointments?: dataValueType;
  averageAppointmentsPerDay?: dataValueType;
}

export const CompareLocationsTable = ({ filters }: CompareLocationsTableProps) => {
  const { activeTab } = useAppointmentAnalyticsV2ShallowStore('activeTab');
  const [pageConfig, setPageConfig] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });

  const {
    data: LocationsViewTableData,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = AnalyticsGatewayV1.Queries.useGetAppointmentLocationsViewQuery(
    {
      ...filters,
      pageSize: pageConfig.pageSize,
    },
    {
      enabled: !!filters.startDate && !!filters.endDate && filters.locationId && filters.locationId?.length > 0,
    }
  );

  // Scroll to top when component mounts
  useScrollToTop();

  // Reset to page 1 when filters change
  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, pageNumber: 1 }));
  }, [filters, pageConfig.pageSize]);

  // Fetch next page when navigating forward to an unloaded page
  useEffect(() => {
    if (!LocationsViewTableData?.pages[pageConfig.pageNumber - 1]) {
      fetchNextPage();
    }
  }, [LocationsViewTableData?.pages, fetchNextPage, pageConfig.pageNumber]);

  const getHasNext = () => {
    const pageData = LocationsViewTableData?.pages[pageConfig.pageNumber - 1]?.data ?? [];
    return hasNextPage && pageData.length === pageConfig.pageSize;
  };

  const rows = useMemo((): LocationRowData[] => {
    return (LocationsViewTableData?.pages[pageConfig.pageNumber - 1]?.data ?? []).map((row: any) => {
      if (activeTab === APPOINTMENT_TABS.UPCOMING) {
        return {
          key: row.key ?? '',
          scheduledAppointments: {
            value: Number(row.scheduledAppointments ?? 0),
            change: Number(row.trends?.scheduledAppointments ?? 0),
          },
          confirmedAppointments: {
            value: Number(row.confirmedAppointments ?? 0),
            change: Number(row.trends?.confirmedAppointments ?? 0),
          },
          atRiskAppointments: {
            value: Number(row.atRiskAppointments ?? 0),
            change: Number(row.trends?.atRiskAppointments ?? 0),
          },
        };
      } else {
        return {
          key: row.key ?? '',
          scheduledAppointments: {
            value: Number(row.scheduledAppointments ?? 0),
            change: Number(row.trends?.scheduledAppointments ?? 0),
          },
          confirmedAppointments: {
            value: Number(row.confirmedAppointments ?? 0),
            change: Number(row.trends?.confirmedAppointments ?? 0),
          },
          completedAppointments: {
            value: Number(row.completedAppointments ?? 0),
            change: Number(row.trends?.completedAppointments ?? 0),
          },
          cancelledAppointments: {
            value: Number(row.cancelledAppointments ?? 0),
            change: Number(row.trends?.cancelledAppointments ?? 0),
          },
          averageAppointmentsPerDay: {
            value: Number(row.averageAppointmentsPerDay ?? 0),
            change: Number(row.trends?.averageAppointmentsPerDay ?? 0),
          },
        };
      }
    });
  }, [LocationsViewTableData, pageConfig.pageNumber, activeTab]);

  const COLUMNS = useMemo(() => getColumns(activeTab), [activeTab]);

  const colConfig: ColumnConfig[] = [
    {
      id: 'locationName',
      Header: 'Location',
      accessor: 'key',
      disableSortBy: true,
      width: 250,
      minWidth: 100,
      maxWidth: 300,
      cellRenderer: (cellValue) => (
        <div
          css={{
            width: '100%',
            height: '100%',
            margin: theme.spacing(-1, -2),
            padding: theme.spacing(1, 2),
            boxSizing: 'content-box',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Text weight='bold'>{cellValue}</Text>
        </div>
      ),
      sticky: 'left',
    },

    ...COLUMNS.map((col, idx) => ({
      id: col.id,
      Header: col.Header,
      accessor: (item: LocationRowData) => item[col.accessor as keyof LocationRowData] as dataValueType,
      sortType: 'number' as const,
      width: 250,
      minWidth: 100,
      maxWidth: 300,
      cellRenderer: (cellValue: dataValueType) => (
        <CellRenderer
          cellValue={cellValue}
          startAdornment={col.startAdornment}
          endAdornment={col.endAdornment}
          isLastColumn={idx === COLUMNS.length - 1}
        />
      ),
    })),
  ];

  const tableProps: TableProps<LocationRowData> = {
    data: rows,
    isLoading: isLoading || isFetching,
    tableInstanceId: 'compare-locations-table',
    hasFilterColumns: true,
    hasResizeColumns: true,
    colConfig,
    isPaginated: true,
    hasGlobalSearch: true,
    manualPaginationConfig: {
      handleChange: (action: 'next' | 'prev') => {
        if (action === 'next') {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber + 1 }));
        } else {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber - 1 }));
        }
      },
      hasNext: getHasNext(),
      hasPrevious: pageConfig.pageNumber > 1,
      page: pageConfig.pageNumber,
      defaultRowsPerPage: pageConfig.pageSize,
      onNumRowsChange: (num) => {
        setPageConfig({ pageNumber: 1, pageSize: num });
      },
      rowsPerPageOptions: [25, 50, 75, 100],
    },
  };

  return <Table {...tableProps} />;
};

const getColorConfig = (change: number): { color: string; backgroundColor: string; icon?: IconName } => {
  if (change > 0) {
    return {
      color: theme.colors.success50,
      backgroundColor: theme.colors.success5,
      icon: 'trend-rise',
    };
  } else if (change < 0) {
    return {
      color: theme.colors.critical50,
      backgroundColor: theme.colors.critical5,
      icon: 'trend-fall',
    };
  } else {
    return {
      color: theme.colors.neutral50,
      backgroundColor: theme.colors.neutral10,
      icon: undefined,
    };
  }
};

const CellRenderer = (props: {
  cellValue: dataValueType;
  startAdornment?: string;
  endAdornment?: string;
  isLastColumn?: boolean;
}) => {
  const { cellValue, startAdornment, endAdornment } = props;
  const { color, backgroundColor, icon } = getColorConfig(cellValue?.change ?? 0);
  const changeValue = Math.abs(cellValue?.change ?? 0);
  return (
    <div
      css={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        width: '100%',
        color: theme.colors.black,
        margin: theme.spacing(-1, -2),
        padding: theme.spacing(1, 2),
        boxSizing: 'content-box',
      }}
    >
      <Text
        weight='bold'
        css={{
          color: 'inherit',
        }}
      >
        {startAdornment}
        {formatters.value(cellValue?.value ?? 0)}
        {endAdornment}
      </Text>
      {changeValue !== 0 ? (
        <span
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.5, 1),
            borderRadius: theme.borderRadius.large,
            color,
            backgroundColor: backgroundColor,
          }}
        >
          {icon && <Icon name={icon} size={12} css={{ marginRight: theme.spacing(0.5) }} />}
          <Text
            size='small'
            css={{
              color: 'inherit',
            }}
          >
            {changeValue}%
          </Text>
        </span>
      ) : (
        <span
          css={{
            minWidth: 60,
            minHeight: 26,
            boxSizing: 'border-box',
          }}
        />
      )}
    </div>
  );
};
